/**
 * Email cache / identity-scoped storage-key names, shared between the email
 * module writers (useFolderMessages / useAccountToggle / useCachedMessages via
 * modules/email/constants.inboxCacheKey, useOptimisticOverlay, useEmailDrafts,
 * AnalysisView, senderStrikes, hiddenSenders) and the identity-boundary purge
 * in features/auth (clearEmailCaches + clearUserSpecificStorage). Hoisted to
 * @/lib — importable by both slices without inverting the feature→module
 * direction — so a key rename can never silently decouple the purge from the
 * writers (#5276). String values are legacy and load-bearing (email/AGENTS.md
 * → Key Constraints): centralize the definition, never change the value.
 */

/**
 * sessionStorage — legacy GLOBAL (not instance-scoped) folder-view rows /
 * fetched flags. Predated the later per-instance scoping work and caused
 * cross-instance data bleed (#5557: a second Email module instance would
 * instantly render the first instance's Sent/Trash/Archived/Snoozed rows).
 * useFolderMessages no longer WRITES these — it writes the per-instance
 * `EMAIL_FOLDER_CACHE_PREFIX`/`EMAIL_FOLDER_FETCHED_PREFIX` keys below. These
 * two stay as a one-time migration READ source (the first instance to mount
 * post-upgrade claims + immediately retires the legacy blob so a second
 * instance can't also inherit it) and as a purge-sweep target for a blob that
 * was never claimed.
 */
export const EMAIL_FOLDER_CACHE_KEY = 'email_folder_cache';
export const EMAIL_FOLDER_FETCHED_KEY = 'email_folder_fetched';
/** sessionStorage prefix — per-instance folder-view rows; full key `${prefix}${instanceId}` (#5557). */
export const EMAIL_FOLDER_CACHE_PREFIX = 'xavior:email.folder-cache:';
/** sessionStorage prefix — per-instance per-folder fetched flags; full key `${prefix}${instanceId}` (#5557). */
export const EMAIL_FOLDER_FETCHED_PREFIX = 'xavior:email.folder-fetched:';
/** sessionStorage prefix — per-instance inbox rows; full key is `${prefix}${instanceId}`. */
export const EMAIL_INBOX_CACHE_PREFIX = 'xavior:email.inbox-cache:';
/** sessionStorage prefix — per-instance drafts list; full key `${prefix}${instanceId}` (#5560). Lazy-seeds the Drafts tab badge count + stale-while-revalidate DraftsView rows on re-entry. */
export const EMAIL_DRAFTS_CACHE_PREFIX = 'xavior:email.drafts-cache:';
/** sessionStorage prefix — per-instance optimistic-mutation op ledger (id → op → expiresAt); full key `${prefix}${instanceId}` (#5558). */
export const EMAIL_OPTIMISTIC_OVERLAY_PREFIX = 'xavior:email.optimistic-ops:';
/** sessionStorage prefix — per-instance pending-delete draft-id guard (id → expiresAt); full key `${prefix}${instanceId}` (#5558). */
export const EMAIL_DELETED_DRAFT_IDS_PREFIX = 'xavior:email.deleted-draft-ids:';
/** localStorage — AnalysisView name→contact-id map, nested per workspace id. */
export const ANALYSIS_CONTACT_CACHE_KEY = 'xavior:analysis.contactCache';
/**
 * localStorage — per-sender dismissal strike counts (senderStrikes.ts). Holds
 * the prior identity's sender addresses (minor PII); purged on logout only
 * (clearUserSpecificStorage), NOT on container switch — same-user preference.
 */
export const EMAIL_DISMISSED_SENDERS_KEY = 'xavior:email.dismissedSenders';
/**
 * localStorage — hidden sender addresses (modules/email/constants
 * HIDDEN_SENDERS_KEY / utils/hiddenSenders.ts). Same logout-only purge
 * rationale as EMAIL_DISMISSED_SENDERS_KEY.
 */
export const EMAIL_HIDDEN_SENDERS_KEY = 'email_hidden_senders';
/**
 * localStorage prefix — durable per-card "sent" markers for chat email draft
 * cards (features/chat EmailDraftCard, #4982); full key is
 * `${prefix}${cardKey}`. Purged on logout only (clearUserSpecificStorage):
 * markers are per-user duplicate-send guards shared across that user's
 * containers, so a container switch must NOT wipe them.
 */
export const EMAIL_SENT_MARKER_PREFIX = 'xavior:email-sent:';
